import { useEffect, useState, useCallback } from "react";

/**
 * Wraps the browser's `beforeinstallprompt` event (Chrome/Edge/Android).
 * iOS Safari never fires this event — there is no programmatic install API
 * there, so callers should show manual "Share -> Add to Home Screen"
 * instructions instead when `canInstall` is false and `isIos` is true.
 */
export default function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(
    () =>
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
  );

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === "accepted";
  }, [deferredPrompt]);

  const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);

  return { canInstall: !!deferredPrompt, installed, isIos, promptInstall };
}
